import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, alpha } from "@mui/material";
import { NorthEastRounded } from "@mui/icons-material";
import color from "../../Constants/colors";
import DashboardCard from "./DashboardCard";

type PartnerKey = "claude" | "openai" | "mistral" | "alan";

interface AiPartner {
  key: PartnerKey;
  name: string;
  logo: string;
  url: string;
  /** Brand colour for the tile glow and hover; logos keep their own colours. */
  brand: string;
  /** The logo is already a filled tile, so it fills the logo box. */
  fullBleed?: boolean;
}

const PARTNERS: AiPartner[] = [
  {
    key: "claude",
    name: "Claude",
    logo: "/claude_logo.png",
    url: "https://claude.ai",
    brand: "#D97757",
  },
  {
    key: "openai",
    name: "OpenAI",
    logo: "/openai_logo.png",
    url: "https://chatgpt.com",
    brand: "#10A37F",
  },
  {
    key: "mistral",
    name: "Mistral AI",
    logo: "/mistral-logo.png",
    url: "https://chat.mistral.ai",
    brand: "#FA520F",
  },
  {
    key: "alan",
    name: "Alan",
    logo: "/alan-icon.png",
    url: "https://app.alan.de",
    brand: "#13365A",
    fullBleed: true,
  },
];

const hostOf = (url: string) => new URL(url).host;

const PartnerTile: React.FC<{ partner: AiPartner }> = ({ partner }) => {
  const { t } = useTranslation("home");
  const { brand } = partner;

  return (
    <Box
      component="a"
      href={partner.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${partner.name} – ${t("hub.modules.external")}`}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        overflow: "hidden",
        borderRadius: "14px",
        border: `1px solid ${color.gray200}`,
        background: `radial-gradient(circle at 0% 0%, ${alpha(brand, 0.14)}, transparent 55%), ${color.white}`,
        textDecoration: "none",
        transition:
          "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(brand, 0.5),
          boxShadow: `0 12px 28px ${alpha(brand, 0.18)}`,
        },
        "&:hover .partner-arrow": {
          transform: "translate(2px, -2px)",
          color: brand,
        },
        "&:focus-visible": {
          outline: `2px solid ${brand}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ flex: 1, p: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              p: partner.fullBleed ? 0 : 1,
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: color.white,
              border: partner.fullBleed ? 0 : `1px solid ${color.gray200}`,
              boxShadow: `0 4px 10px ${alpha(brand, 0.16)}`,
            }}
          >
            <Box
              component="img"
              src={partner.logo}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <NorthEastRounded
            className="partner-arrow"
            sx={{
              fontSize: 18,
              color: color.gray400,
              transition: "transform 0.15s ease, color 0.15s ease",
            }}
          />
        </Box>

        <Typography
          sx={{
            mt: 1.5,
            fontSize: 15,
            fontWeight: 700,
            lineHeight: "20px",
            color: color.primary_dark,
          }}
        >
          {partner.name}
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 13,
            lineHeight: "18px",
            color: color.gray500,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {t(`hub.partners.items.${partner.key}`)}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderTop: `1px solid ${color.gray100}`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            flexShrink: 0,
            borderRadius: "50%",
            backgroundColor: brand,
          }}
        />
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 600,
            color: color.gray600,
          }}
        >
          {hostOf(partner.url)}
        </Typography>
      </Box>
    </Box>
  );
};

/** The AI providers the hub runs on, each linking to its own app. */
const AiPartnersSection: React.FC = () => {
  const { t } = useTranslation("home");

  return (
    <DashboardCard
      title={t("hub.partners.title")}
      subtitle={t("hub.partners.subtitle")}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {PARTNERS.map((partner) => (
          <PartnerTile key={partner.key} partner={partner} />
        ))}
      </Box>
    </DashboardCard>
  );
};

export default AiPartnersSection;
